<script setup>
import { computed, ref, watch } from "vue";
import { languageName } from "i18n";
import { methodName } from "lib/booking/modules/util";
import { formatAddress, sortByKey } from "lib/modules/util";
import ServiceItem from "lib/components/ServiceItem";
import { addSrvsSchema } from "lib/modules/schema";

const props = defineProps(["org", "userId", "now"]);

const url = new URL(document.location);

const enabledUsers = props.org.users.filter((u) => u.enabled && u.langs.length > 0 && (props.userId === null || props.userId === u.user_id));

const activeServices = sortByKey(props.org.srvs.filter((srv) => {
	if (!srv.enabled)
		return false;

	if (!srv.office && !srv.video && !srv.phone)
		return false;

	if (props.userId !== null && !srv.puser_select)
		return false;

	if (!srv.users.some((userId) => enabledUsers.find((user) => user.user_id === userId)))
		return false;

	if (srv.visibility === "private" || (srv.visibility === "personal" && props.userId === null))
		return false;

	return true;
}), "title");

const activeUsers = enabledUsers.filter((u) => activeServices.some((s) => s.users.includes(u.user_id)));

const activeMethods = ["video", "phone", "office"].filter((met) => activeServices.some((s) => s[met])).map((method) => ({ method, name: methodName(method) }));

const activeBranches = sortByKey([props.org, ...props.org.branches]
	.map((b) => ({ branch_id: (b.branch_id || null), title: formatAddress(b) }))
	.filter((b) => activeUsers.some((u) => u.branch_id === b.branch_id && activeServices.some((s) => s.office && s.users.includes(u.user_id)))), "title");

const activeLanguages = sortByKey(Array.from(new Set(activeUsers.flatMap((u) => u.langs)))
	.map((lang_id) => ({ lang_id, name: languageName(lang_id) })), "name");

function getLocation() {
	let branchId = url.searchParams.get("branch_id");
	const method = url.searchParams.get("method");

	if (branchId === "null")
		branchId = null;

	if (method === "office" && activeMethods.some((m) => m.method === method) && activeBranches.some((b) => b.branch_id === branchId))
		return "office:" + branchId;
	else if (["video", "phone"].includes(method) && activeMethods.some((m) => m.method === method))
		return method;

	return null;
}

function getLanguage() {
	const langId = url.searchParams.get("lang_id");

	if (langId !== null && activeLanguages.some((l) => l.lang_id === langId))
		return langId;

	return null;
}

const inputFilter = ref("");
const selectedLocation = ref(getLocation());
const selectedLanguage = ref(getLanguage());

const selectedMethod = computed(() => selectedLocation.value === null ? null : selectedLocation.value.split(":")[0]);

const selectedBranch = computed(() => {
	if (selectedLocation.value === null)
		return;

	const branchId = selectedLocation.value.split(":")[1];

	return branchId === "null" ? null : branchId; // eslint-disable-line consistent-return
});

const filteredServices = computed(() => {
	const search = inputFilter.value.toLowerCase();
	let users = activeUsers;

	if (selectedMethod.value === "office")
		users = users.filter((u) => u.branch_id === selectedBranch.value);

	if (selectedLanguage.value !== null)
		users = users.filter((u) => u.langs.includes(selectedLanguage.value));

	return activeServices.filter((srv) => {
		const pusers = users.filter((u) => srv.users.includes(u.user_id));

		if (pusers.length === 0)
			return false;

		if (search !== "" && (srv.title.toLowerCase() + srv.descr.toLowerCase()).indexOf(search) === -1)
			return false;

		if (["video", "phone", "office"].includes(selectedMethod.value) && !srv[selectedMethod.value])
			return false;

		return true;
	});
});

const bookingParams = computed(() => {
	const sp = new URLSearchParams();

	if (selectedMethod.value !== null)
		sp.set("method", selectedMethod.value);

	if (typeof selectedBranch.value !== "undefined")
		sp.set("branch_id", selectedBranch.value);

	if (selectedLanguage.value !== null)
		sp.set("lang_id", selectedLanguage.value);

	if (props.userId !== null)
		sp.set("user_id", props.userId);

	const str = sp.toString();

	return str.length ? "?" + str : "";
});

watch([selectedMethod, selectedBranch], ([method, branchId]) => {
	if (method === null)
		url.searchParams.delete("method");
	else
		url.searchParams.set("method", method);

	if (typeof branchId === "undefined")
		url.searchParams.delete("branch_id");
	else
		url.searchParams.set("branch_id", branchId);

	history.replaceState(null, "", url.toString());
});

watch(selectedLanguage, (val) => {
	if (val === null)
		url.searchParams.delete("lang_id");
	else
		url.searchParams.set("lang_id", val);

	history.replaceState(null, "", url.toString());
});

addSrvsSchema(activeServices.map((srv) => ({
	...srv,
	rating: props.org.rating,
	rating_cnt: props.org.rating_cnt,
	org_name: props.org.name
})));
</script>

<template>
	<div v-if="activeServices.length > 0" class="filters">
		<label v-if="activeMethods.length > 1 || activeBranches.length > 1" class="vui-select">
			<select v-model="selectedLocation" :title="$gettext('Select location')">
				<option :value="null">{{ $gettext("All locations") }}</option>
				<template v-for="m in activeMethods">
					<optgroup v-if="m.method === 'office' && activeBranches.length > 0" :label="m.name">
						<option v-for="b in activeBranches" :value="`office:${b.branch_id}`">{{ b.title }}</option>
					</optgroup>
					<option v-else :value="m.method">{{ m.name }}</option>
				</template>
			</select>
		</label>
		<label v-if="activeLanguages.length > 1" class="vui-select">
			<select v-model="selectedLanguage" :title="$gettext('Select language')">
				<option :value="null">{{ $gettext("All languages") }}</option>
				<option v-for="l in activeLanguages" :value="l.lang_id">{{ l.name }}</option>
			</select>
		</label>
		<label class="vui-input">
			<div class="input-wrapper">
				<i class="prefix fa-regular fa-magnifying-glass"></i>
				<input
					v-model.trim="inputFilter"
					:placeholder="$gettext('Search')"
					type="text"
				>
			</div>
		</label>
	</div>
	<div class="service-list grid gap-20">
		<div v-if="activeServices.length === 0" class="no-services">{{ $gettext("No active services.") }}</div>
		<div v-else-if="filteredServices.length === 0" class="no-services">{{ $gettext("No matches found.") }}</div>
		<service-item
			v-for="service in filteredServices"
			:key="service.srv_id"
			:srv="service"
			:org="org"
			line-clamp="2"
		>
			<a :href="`booking/${service.srv_id}${bookingParams}`" class="vui-btn" :class="{ disabled: org.eos < now }">
				{{ $gettext("See availability") }}
			</a>
		</service-item>
	</div>
</template>
